import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { logos } from '@assets/logos';
import { icons } from '@assets/icons';
import { Navigate, useLocation } from 'react-router-dom';
import useSWR, { useSWRConfig } from 'swr';
import fetch from 'unfetch';
import { useSetRecoilState } from 'recoil';
import { css } from '@emotion/react';

import { dialogContentsState, popupContentsState } from '@components/templates/Dialog';
import { ErrorCode, ErrorMessage } from '@variables/error';
import { PrimaryButton, Sub1Button } from '@components/atoms';
import { ADMIN_ACCESS_TOKEN_KEY, BASE_URL, PRODUCT_TITLE } from '../../../App';
import { MyAccountField } from '@components/templates';
import { submitDebounce } from '../../../utils/lib';

const StyledWrapper = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 80px;
  border-bottom: 2px solid #7fe200;
  padding: 0 40px;
`;

const StyledLogoWrapper = styled.span`
  position: relative;
  display: flex;
  padding-left: 26px;
  align-items: baseline;

  & > img {
    position: absolute;
    top: 6px;
    left: 0;
    width: 26px;
    height: 26px;
  }

  & > span:nth-of-type(1) {
    font-size: 24px;
    font-weight: 700;
    color: #222222;
    padding-left: 12px;
  }

  & > span:nth-of-type(2) {
    font-size: 16px;
    font-weight: 700;
    color: #777777;
    padding-left: 5px;
  }

  & .superscript {
    font-size: 14px;
    vertical-align: super;
  }
`;

const StyledMyAccount = styled.span`
  display: flex;
  align-items: center;
  cursor: pointer;
  & > span[data-type='myaccount'] {
    width: 40px;
    height: 40px;
    :before {
      content: url(${icons.my_account});
    }
    :hover:before {
      content: url(${icons.my_account_colored});
    }
  }

  & > span {
    font-size: 14px;
    font-weight: 700;
    color: #555555;
    text-decoration: underline;
    padding-right: 20px;

    &:hover {
      cursor: pointer;
    }
  }
`;

const fetcher = (method = 'POST', url: string, accessToken: string) =>
  fetch(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
    .then((r) => {
      return r.json();
    })
    .catch((e) => {
      console.log('error!!!!!', e);
    });

const PageHeader = () => {
  const token = localStorage.getItem(ADMIN_ACCESS_TOKEN_KEY) as string;
  const { pathname } = useLocation();
  const [isSignedOut, setIsSignedOut] = useState(false);

  const setPopupContents = useSetRecoilState(popupContentsState);
  const setDialogContents = useSetRecoilState(dialogContentsState);

  const url = `api/logout`;
  const { data, error } = useSWR(isSignedOut ? [{ method: 'POST', url, accessToken: token }] : null);

  if (data) {
    if (data.code === 'UNAUTHORIZED' || data.code === '200') {
      localStorage.removeItem(ADMIN_ACCESS_TOKEN_KEY);
      setPopupContents({ type: 'hidden' });
      setDialogContents({ type: 'hidden' });
      return <Navigate to={'/'} replace={true} />;
    }
    setIsSignedOut(false);
  }

  const signOut = () => {
    setIsSignedOut(true);
  };

  const openSignOutDialog = () => {
    setDialogContents({
      type: 'plain',
      body: <>Are you sure you want to Sign out?</>,
      footer: (
        <>
          <Sub1Button onClick={() => setDialogContents({ type: 'hidden' })}>NO</Sub1Button>
          <PrimaryButton onClick={submitDebounce(signOut)}>YES</PrimaryButton>
        </>
      ),
    });
  };

  const openMyAccountDialog = () => {
    setPopupContents({
      type: 'plain',
      header: (
        <>
          <h1 style={{ color: '#333333' }}>My Account</h1>
          <Sub1Button styles={CloseButtonStyle} onClick={() => setPopupContents({ type: 'hidden' })} />
        </>
      ),
      body: <MyAccountField token={token} />,
      footer: (
        <Sub1Button styles={SignoutButtonStyle} onClick={() => openSignOutDialog()}>
          Sign out
        </Sub1Button>
      ),
    });
  };
  return (
    <StyledWrapper>
      <StyledLogoWrapper>
        <img src={logos.logo} alt="" />
        <span>
          {PRODUCT_TITLE}
          {PRODUCT_TITLE.includes('StroCare') ? <sub className="superscript">&#8482;</sub> : <></>}
        </span>
        <span>Admin</span>
      </StyledLogoWrapper>
      <StyledMyAccount>
        {pathname !== '/' && pathname !== '/register' && (
          <>
            <span onClick={openSignOutDialog}>Sign out</span>
            <span data-type="myaccount" onClick={openMyAccountDialog} />
          </>
        )}
      </StyledMyAccount>
    </StyledWrapper>
  );
};

export const SignoutButtonStyle = css`
  width: auto;
  height: 48px;
  border-width: 0;
  color: #999;
  font-size: 14px;
  text-decoration: underline;
  &:hover,
  &:active {
    color: #000;
    background-color: transparent;
  }
`;

export const CloseButtonStyle = css`
  position: absolute;
  top: 30px;
  right: 30px;
  width: 30px;
  height: 30px;
  border-width: 0;
  background-repeat: no-repeat;
  background-position: 50% 50%;
  background-image: url(${icons.close});
  background-size: cover;
  color: #999;
  &:hover,
  &:active {
    background-image: url(${icons.close});
    background-size: cover;
    background-color: transparent;
  }
`;

export default PageHeader;
